import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import CustomTextInput from "../../../components/CustomTextInput";
import { Helpers } from "../../../helpers/Helpers";
import { AddEscalationRequest } from "../../../models/request/escalation/AddEscalationRequest";
import { AddEscalationResponse, GetEscalationByIdResponse } from "../../../models/response/escalation/GetEscalationListPagingAndFilterResponse";
import { storeActivityIndicator } from "../../../redux/reducers/authSlice";
import { ConstantService } from "../../../services/ConstantService";
import { EscalationService } from "../../../services/EscalationService";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import EscalationItems, { COST_ITEM_CATEGORY, EscalationItemRow } from "./Components/EscalationItems";

const EscalationAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pageName = "Ayarlar > Eskalasyon Dağılımı";
    const [searchParams] = useSearchParams();

    const [escalationId, setEscalationId] = useState<string | undefined>(undefined);

    // Donem ve islem tarihi kullanicidan alinmaz: ikisi de pasif gosterilir.
    const [period, setPeriod] = useState<number>(new Date().getFullYear());
    const [transactionDateStr, setTransactionDateStr] = useState<string>(Helpers.dateParser(new Date().toISOString()));

    const [description, setDescription] = useState<string | undefined>(undefined);
    const [items, setItems] = useState<EscalationItemRow[]>([]);

    useEffect(() => {
        getData(searchParams.get("id"));
    }, [])

    /**
     * Yeni kayitta grid bos gelmesin: tanimli maliyet kalemlerinden varsayilan
     * yuzdesi olanlar hazir satir olarak gelir, kullanici uzerinde degisiklik yapar.
     */
    const varsayilanSatirlar = async () => {
        try {
            var kalemler: any[] = await ConstantService.getConstantList(COST_ITEM_CATEGORY);

            var hazir = (kalemler ?? [])
                .filter(x => !Helpers.isEmpty(x.additionalValue1))
                .map(function (x): EscalationItemRow {
                    return {
                        escalationItemId: Helpers.generateUUID(true),
                        costItemId: x.constantId,
                        costItemName: x.value,
                        value: x.additionalValue1,
                        description: undefined,
                        isSelected: false
                    }
                });

            setItems(hazir);
        } catch {
            // Varsayilan gelmezse ekran bos gridle acilir, kullanici elle ekler.
            setItems([]);
        }
    }

    const getData = async (_id: string | null) => {
        try {
            dispatch(storeActivityIndicator(true));

            if (!_id) {
                await varsayilanSatirlar();
                return;
            }

            var response: GetEscalationByIdResponse = await EscalationService.getEscalationById(_id);

            if (!response.isSuccess) {
                toast.error(response.message);
                return;
            }

            setEscalationId(response.data.escalationId);
            setPeriod(response.data.period ?? new Date().getFullYear());
            setTransactionDateStr(response.data.transactionDateStr ?? '');
            setDescription(response.data.description);

            setItems((response.data.items ?? []).map(function (x): EscalationItemRow {
                return {
                    escalationItemId: x.escalationItemId,
                    costItemId: x.costItemId,
                    costItemName: x.costItemName,
                    value: x.value?.toString(),
                    description: x.description,
                    isSelected: false
                }
            }));
        } catch (error: any) {
            toast.error(error);
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const save = async () => {
        try {
            if (items.length == 0) {
                toast.error('En az bir maliyet kalemi ekleyiniz.');
                return;
            }

            dispatch(storeActivityIndicator(true));

            var obj: AddEscalationRequest = {
                escalationId: escalationId,
                period: period,
                description: description,
                items: items.map(x => ({
                    // Ekranda uretilen gecici kimlik ("newrecrd-...") gecerli bir kimlik degil;
                    // yeni satirlarda bos gonderilir, sunucu kendi kimligini uretir.
                    escalationItemId: x.escalationItemId?.startsWith('newrecrd') ? undefined : x.escalationItemId,
                    costItemId: x.costItemId,
                    value: x.value,
                    description: x.description
                }))
            };

            var response: AddEscalationResponse = await EscalationService.addEscalation(obj);

            if (!response.isSuccess) {
                toast.error(response.message);
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success(response.message);

            dispatch(storeActivityIndicator(false));
            navigate(Helpers.getGoBackUrl());
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <div>
                        <KTCard className="ebbe-card">
                            <KTCardBody>
                                <h4 className="mb-10 text-black">Genel Bilgiler</h4>

                                <div className="row">
                                    <div className="col-md-3">
                                        <CustomTextInput isDisabled labelText="Dönem" value={period.toString()} />
                                    </div>

                                    <div className="col-md-3">
                                        <CustomTextInput isDisabled labelText="İşlem Tarihi" value={transactionDateStr} />
                                    </div>

                                    <div className="col-md-6">
                                        <CustomTextInput labelText="Açıklama" value={description} setValue={setDescription} />
                                    </div>
                                </div>
                            </KTCardBody>
                        </KTCard>
                    </div>

                    <EscalationItems items={items} setItems={setItems} />

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" onClick={save}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default EscalationAdd;
